import { yupResolver } from '@hookform/resolvers/yup';
import CheckIcon from '@mui/icons-material/Check';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as yup from 'yup';

import { DefaultMessageField } from '@features/employerAccount';
import { useEditTemplate } from '../hooks/useEditTemplate';
import { Close, HelpOutline } from '@mui/icons-material';
import { EditIcon } from '../../../vectors/index';
import BundledEditor from '@utils/BundledEditor';
import { EnterpriseTemplate } from '../types';
import { getStrippedText } from '@utils/helper';
import AIPromptModal from '@features/messaging/components/AIPromptModal/AIPromptModal';
import AssistantIcon from '@mui/icons-material/Assistant';
import { AITooltipText } from '@features/messaging';

type TemplateUpdateDefaultMessageModalProps = {
  handleClose: () => void;
  field: DefaultMessageField | null;
  templateData: EnterpriseTemplate;
  content: string;
};

type FormData = {
  message: string;
};

const schema: yup.ObjectSchema<FormData> = yup
  .object({
    message: yup
      .string()
      .required('Message is required')
      .test('not-empty-html', 'This field cannot be empty', (value) => {
        if (!value) return false;
        return getStrippedText(value).length > 0;
      }),
  })
  .required();

const TemplateUpdateDefaultMessageModal: FC<
  TemplateUpdateDefaultMessageModalProps
> = ({ handleClose, field, templateData, content }) => {
  const [errorText, setErrorText] = useState('');
  const [showAiPromptModal, setShowAiPromptModal] = useState(false);
  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      message: '',
    },
  });

  const { editTemplate, isUpdatingTemplate } = useEditTemplate({
    setErrorText,
  });

  const getTitle = () => {
    switch (field) {
      case 'default_request_more_info_message':
        return 'Update request more info message';
      case 'default_interview_block_message':
        return 'Update interview block message';
      case 'default_interview_confirm_message':
        return 'Update interview confirm message';
      case 'default_open_interview_message':
        return 'Update open interview message';
      case 'default_interview_message':
        return 'Update interview message';
      case 'default_saved_message':
        return 'Update saved message';
      case 'default_declined_message':
        return 'Update declined message';
      case 'default_hire_message':
        return 'Update hired message';
      case 'default_archive_message':
        return 'Update archive message';
      default:
        return 'Update Default Message';
    }
  };

  const handleInsertAIContent = (content: string) => {
    setValue('message', content);
    setShowAiPromptModal(false);
  };

  const onSubmit = (values: FormData) => {
    const options = {
      onSuccess: (data: { message: string }) => {
        toast.success(data.message);
        setValue('message', '');
        handleClose();
      },
    };
    editTemplate(
      { [field!]: values.message, template_id: templateData?.id },
      options,
    );
  };

  useEffect(() => {
    if (content) {
      setValue('message', content);
    }
  }, [content, field]);

  return (
    <>
      <Dialog
        open={Boolean(field)}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        disableEnforceFocus
      >
        <DialogTitle>
          <IconButton
            sx={{ position: 'absolute', top: 5, right: 5 }}
            onClick={handleClose}
          >
            <Close />
          </IconButton>
          <br />
          <EditIcon />
          <Typography variant="h4" sx={{ pt: 3 }}>
            {getTitle()}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <form
            onSubmit={handleSubmit(onSubmit)}
            style={{ marginBottom: '10px' }}
          >
            <Stack
              spacing={3}
              sx={{
                pt: 3,
                '& p': {
                  ml: 1,
                  fontSize: '15px',
                },
              }}
            >
              {errorText && <Alert severity="error">{errorText}</Alert>}
              <Controller
                control={control}
                name="message"
                render={({ field }) => (
                  <Stack spacing={1}>
                    <BundledEditor
                      {...field}
                      onEditorChange={(newValue: string) =>
                        field.onChange(newValue)
                      }
                      onChange={() => {}}
                      init={{
                        height: 200,
                        menubar: false,
                        plugins: ['lists', 'autolink', 'link', 'pagebreak'],
                        toolbar:
                          'spellchecker language spellcheckdialog | styles | bold italic | bullist numlist | link',
                        placeholder: 'Default Message',
                        spellchecker_dialog: true,
                        spellchecker_rpc_url: 'localhost/ephox-spelling',
                        spellchecker_language: 'en_US',
                        spellchecker_active: true,
                        browser_spellcheck: true,
                        contextmenu: false,
                      }}
                    />
                    {Boolean(errors?.message?.message) && (
                      <Typography color="error">
                        {errors?.message?.message}
                      </Typography>
                    )}
                    <Stack
                      mt={1}
                      spacing={2}
                      direction="row"
                      alignItems="center"
                      justifyContent="end"
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setShowAiPromptModal(true)}
                        startIcon={<AssistantIcon />}
                      >
                        Write with AI
                      </Button>
                      <Tooltip title={AITooltipText} placement="top" arrow>
                        <HelpOutline color="secondary" fontSize="small" />
                      </Tooltip>
                    </Stack>
                  </Stack>
                )}
              />
              <LoadingButton
                type="submit"
                variant="contained"
                size="small"
                fullWidth
                loading={isUpdatingTemplate}
                loadingPosition="start"
                color="secondary"
                startIcon={<CheckIcon />}
              >
                Save
              </LoadingButton>
            </Stack>
          </form>
        </DialogContent>
      </Dialog>
      {showAiPromptModal && (
        <AIPromptModal
          isOpen={showAiPromptModal}
          handleInsertAIContent={handleInsertAIContent}
          handleClose={() => setShowAiPromptModal(false)}
        />
      )}
    </>
  );
};

export default TemplateUpdateDefaultMessageModal;
